import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
  CheckMark,
  Link,
  Spacer,
  TranslationProvider,
} from '@resy/ui-kitchen';

import {
  pastCutoff,
} from '../../utils/cancel';

import './Canceled.scss';

const cancelSuccessReservation = (venue) => (
  <TranslationProvider
    translationKey="cancel_reservation_confirmation_message"
    translationValues={{ venueName: venue }}
  >
    <p>{`Your reservation at ${venue} has been cancelled.`}</p>
  </TranslationProvider>
);

const cancelSuccessWithCredit = () => (
  <TranslationProvider
    translationKey="cancel_reservation_confirmation_message_credit"
  >
    <p>Your credit will be reflected in your account shortly.</p>
  </TranslationProvider>
);

const cancelSuccessWithRefund = () => (
  <TranslationProvider
    translationKey="cancel_reservation_confirmation_message_refund"
  >
    <p>You&#39;ll receive a refund shortly.</p>
  </TranslationProvider>
);

const cancelSuccessThanks = () => (
  <TranslationProvider
    translationKey="cancel_reservation_confirmation_message_thanks"
  >
    Thanks for letting us know.
  </TranslationProvider>
);

const getCancelSuccess = (reservation) => {
  let additionalSuccessMessage = null;

  const isFree = (reservation.price === null || reservation.price === 0);
  const isEligibleForRefund = !(
    reservation.cancellation.date_refund_cut_off === null
    || pastCutoff(reservation.cancellation.date_refund_cut_off)
  );
  const isEligibleForCredit = !(
    reservation.cancellation.date_credit_cut_off === null
    || pastCutoff(reservation.cancellation.date_credit_cut_off)
  );

  const successMessage = cancelSuccessReservation(reservation.venue.name);

  if (!isFree && isEligibleForRefund) {
    additionalSuccessMessage = cancelSuccessWithRefund();
  } else if (!isFree && isEligibleForCredit) {
    additionalSuccessMessage = cancelSuccessWithCredit();
  } else if (!isFree) {
    additionalSuccessMessage = cancelSuccessThanks();
  } else {
    additionalSuccessMessage = cancelSuccessThanks();
  }

  return (
    <>
      {successMessage}
      {additionalSuccessMessage}
    </>
  );
};

export const getSuccessLink = (Analytics, reservation, link) => {
  const {
    venue: {
      name: venueName,
    } = {},
  } = reservation;

  const key = 'book_another';

  const txt = `Book another table at ${venueName}?`;

  return (
    <TranslationProvider translationKey={key} translationValues={{ venueName }}>
      <Link className="BookAnotherTableLink" href={link}>
        {txt}
      </Link>
    </TranslationProvider>
  );
};

const Canceled = ({
  Analytics,
  link,
  reservation,
  escHandler,
}) => {
  const handleKeyPress = (event) => {
    event.preventDefault();
    if (event.key === 'Escape') {
      escHandler();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div className="Canceled">
      <CheckMark />
      <Spacer height="15" />
      <TranslationProvider translationKey="canceled">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <h2 tabIndex={0} aria-live="polite" role="status">Cancelled</h2>
      </TranslationProvider>
      <Spacer height="10" />
      {getCancelSuccess(reservation)}
      <Spacer height="30" />
      {getSuccessLink(Analytics, reservation, link)}
      <Spacer height="60" />
    </div>
  );
};

Canceled.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  escHandler: PropTypes.func,
  link: PropTypes.string,
  reservation: PropTypes.shape({
    cancellation: PropTypes.shape({
      fee: PropTypes.shape({
        amount: PropTypes.number,
      }),
    }),
    date_refund_cut_off: PropTypes.string,
    date_credit_cut_off: PropTypes.string,
    fee: PropTypes.shape({
      date_cut_off: PropTypes.string,
    }),
    price: PropTypes.number,
    venue: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
};

Canceled.defaultProps = {
  link: undefined,
  reservation: undefined,
  escHandler: undefined,
};

export default Canceled;
